import { Component, inject } from '@angular/core';
import { ApiLiveSchedule, ApiLiveScheduleList, ApiLiveStream } from '@tytapp/api';
import { BillingService } from '@tytapp/billing';
import { isClientSide, isServerSide } from '@tytapp/environment-utils';
import { LiveStreamsService } from '@tytapp/live-streams';
import { UserService } from '@tytapp/user';
import { Subscription } from 'rxjs';

@Component({
    selector: 'tyt-full-live-schedule',
    standalone: false,
    template: `
            <div class="content">
                <div class="schedule">
                    <header>
                        <mat-icon>podcasts</mat-icon>&nbsp;
                        Live Schedule
                        <mat-button-toggle-group [(ngModel)]="timeZone" aria-label="Time Zone">
                            <mat-button-toggle value="local">Local</mat-button-toggle>
                            <mat-button-toggle value="est">ET</mat-button-toggle>
                            <mat-button-toggle value="pst">PT</mat-button-toggle>
                        </mat-button-toggle-group>
                    </header>
                    @if (hasLiveItems) {
                        <h2>Now</h2>
                        <div class="block live">
                            <span>• Live</span>
                            @for (item of activeLiveEvents; track item) {
                                <a [routerLink]="getStreamLink(item)">
                                    <div class="avatar" [responsiveBackgroundImage]="item.show.logo_square"></div>
                                    {{ item.use_title ? item.title : item.show.name }}
                                    <span>{{ airTimeForItem(item) | date : 'h:mm a' : this.timezone }} {{ generalTimeZoneString }}</span>
                                </a>
                            }
                            @for (item of adhocStreams; track item) {
                                <a routerLink="/live/streams/{{ item.id }}">
                                    <div class="avatar adhoc">
                                        <mat-icon>cell_tower</mat-icon>
                                    </div>
                                    {{ item.title || 'Watch Live' }}
                                </a>
                            }
                        </div>
                    }
                    @if (!liveSchedules) {
                        <app-spinner></app-spinner>
                    }
                    @for (day of liveSchedules; track day) {
                        @if (day.live_schedules.length) {
                            <h2>{{ getDayDisplay(day.day) }}</h2>
                            <div class="block">
                                @for (item of day.live_schedules; track item) {
                                    <a [routerLink]="getStreamLink(item)">
                                        <div class="avatar" [responsiveBackgroundImage]="item.show.logo_square"></div>
                                        {{ item.use_title ? item.title : item.show.name }}
                                        <span>{{ airTimeForItem(item) | date : 'h:mm a' : this.timezone }} {{ generalTimeZoneString }}</span>
                                    </a>
                                }
                            </div>
                        }
                    }
                </div>
                <div class="side">
                    <tyt-plus-ad-block></tyt-plus-ad-block>
                    <tyt-mobile-ad-block></tyt-mobile-ad-block>
                    <tyt-newsletter-module></tyt-newsletter-module>
                </div>
            </div>
    `,
    styles: [`
        @use "styles/constants";
        @use "styles/colors";
        @use "styles/component-base";

        .content {
            display: grid;
            grid-template-columns: 1fr; //1fr so the blockview looks alright.
            gap: constants.$gap-medium;

            @media (max-width: constants.$mobile-width) {
                grid-template-columns: 1fr;
            }
        }

        .schedule, .side {
            display: flex;
            flex-direction: column;
        }

        .side {
            gap: constants.$gap-medium;
        }

        header {
            display: flex;
            align-items: center;
            padding-left: constants.$gap-small;
            background-color: colors.$background2;
            border: 1px solid colors.$translucent;

        > mat-icon:first-child {
            color: colors.$live;
        }

        > mat-button-toggle-group {
            margin-left: auto;
            border-radius: 0;
            border-width: 0 0 0 1px;

            .mat-button-toggle-checked {
            background-color: colors.$accent;
            }
        }
        }

        h2 {
        font-size: constants.$font-size-small;
        padding: constants.$gap-small;
        }

        .block {
            display: flex;
            flex-direction: column;
            gap: constants.$gap-medium;
            padding: constants.$gap-small;
            background-color: colors.$dark-background;
            border: 1px solid colors.$translucent;

            > :is(div, a) {
                display: flex;
                gap: constants.$gap-small;
                align-items: center;
                color: colors.$neutral;

                > span:last-child {
                flex-shrink: 0;
                margin-left: auto;
                }
            }

            &.live {
                border: 2px solid colors.$live;

                > span:first-child {
                background-color: colors.$live;
                margin: - constants.$gap-small;
                padding-left: constants.$gap-small;
                font-size: constants.$font-size-small;
                }
            }
        }

        .avatar {
            flex-shrink: 0;
            width: 2.5em;
            aspect-ratio: 1 / 1;
            background-size: cover;

            &.adhoc {
                display: grid;
                place-items: center;
            }
        }

        @media (max-width: constants.$mobile-width) {
            .content {
                grid-template-columns: 1fr;
            }

            .block.live {
                > span:first-child {
                padding: 0;
                text-align: center;
                }
            }
        }
    `]
})
export class FullLiveScheduleComponent {
    private userService = inject(UserService);
    private billing = inject(BillingService);
    private liveStreamsService = inject(LiveStreamsService);
    private subscriptions = new Subscription();
    private allStreams: ApiLiveStream[];
    private entitled: boolean;
    private timezoneString: string;

    audienceType: string = 'visitor';
    ready: boolean = false;
    timezone: string;
    liveSchedules: ApiLiveScheduleList[];
    otherStreams: ApiLiveStream[];

    async ngOnInit() {
        this.subscriptions.add(this.userService.userChanged.subscribe(user => this.fetchData()));
        this.subscriptions.add(this.billing.entitlementChanged.subscribe(entitled => this.entitled = entitled));
        this.subscriptions.add(this.liveStreamsService.streamsChanged.subscribe(() => this.fetchData()));

        this.emulateNow = new Date('2019-03-22T03:00:00-0800').getTime();
        this.audienceType = this.userService.audienceType;

        await this.fetchSchedule();
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    getEventStream(event: ApiLiveSchedule): ApiLiveStream {
        if (!this.allStreams)
            return null;

        let streams = this.allStreams.filter(x => x.active_live_event && x.active_live_event.id == event.id);
        let premiumStream = streams.filter(x => x.premium)[0];
        let publicStream = streams.filter(x => !x.premium)[0];

        if (this.entitled)
            return premiumStream || publicStream;

        return publicStream || premiumStream;
    }

    async onBlockChanged(): Promise<void> {
    }

    emulateNow: number;

    isEnded(event: ApiLiveSchedule) {
        if (this.getEventStream(event))
            return false;

        let now = Date.now();

        if (this.emulateNow !== undefined)
            now = this.emulateNow;

        let startedAt = 1000 * parseInt(event.air_time_unix);
        return startedAt + 1000 * 60 * 15 < now;
    }

    getTimezoneOffset(d: Date, tz: string) {
        var a = d.toLocaleString("ja", { timeZone: tz }).split(/[\/\s:]/);
        a[1] = (Number(a[1]) - 1).toString();
        var t1 = Date.UTC.apply(null, a);
        var t2 = new Date(d).setMilliseconds(0);
        return (t2 - t1) / 60 / 1000 / 60;
    }

    async fetchSchedule() {
        let options: Record<string, string> = { timeZoneName: 'short' };
        if (isServerSide()) {
            options['timeZone'] = 'America/New_York';
        }

        if (this.useTimezone === 'pst') {
            options['timeZone'] = 'America/Los_Angeles';
        }

        if (this.useTimezone === 'est') {
            options['timeZone'] = 'America/New_York';
        }

        let customOffset = undefined;
        if (options['timeZone']) {
            customOffset = this.getTimezoneOffset(new Date(), options['timeZone']);
        }

        let date = new Date();
        this.timezoneString = date.toLocaleTimeString('en-us', options).split(' ')[2];
        if (isServerSide() || this.useTimezone != 'local')
            this.timezone = this.timezoneString;
        else
            this.timezone = null;

        let schedules = await this.liveStreamsService.getSchedule(customOffset);
        if (schedules) {
            let todayIndex = new Date().getDay();
            todayIndex = 1;

            let days = [
                'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday',
                'Friday', 'Saturday'
            ];
            let reordered = [];
            for (let day = 0; day < 7; ++day) {
                let dayStr = days[(todayIndex + day) % 7].toLowerCase();
                let scheduleItem = schedules.find(x => x.day.toLowerCase() === dayStr);
                if (scheduleItem)
                    reordered.push(scheduleItem);
            }
            schedules = reordered;

            this.liveSchedules = schedules;
            this.updateActiveLiveEvents();
            this.ready = true;
        }
    }

    useTimezone: string = 'local';

    async switchTimezone(tz) {
        this.useTimezone = tz;
        this.liveSchedules = null;
        await this.fetchSchedule();
    }

    get generalTimeZoneString() {
        if (this.timezoneString === 'PST' || this.timezoneString === 'PDT') {
            return 'PT';
        }
        if (this.timezoneString === 'EST' || this.timezoneString === 'EDT') {
            return 'ET';
        }
        return null;
    }

    activeLiveEvents: ApiLiveSchedule[];
    adhocStreams: ApiLiveStream[];

    updateActiveLiveEvents() {
        let events = {};

        if (!this.allStreams) {
            this.activeLiveEvents = [];
            return;
        }

        for (let stream of this.allStreams) {
            if (stream.active_live_event) {
                if (!events[stream.active_live_event.id])
                    events[stream.active_live_event.id] = stream.active_live_event;
            }
        }

        this.activeLiveEvents = Object.keys(events).map(x => events[x]);
    }

    get hasLiveItems() {
        return (this.adhocStreams && this.adhocStreams.length > 0)
            || (this.activeLiveEvents && this.activeLiveEvents.length > 0);
    }

    getDayDisplay(day: string) {
        const days = {
            sunday: 'Sunday',
            monday: 'Monday',
            tuesday: 'Tuesday',
            wednesday: 'Wednesday',
            thursday: 'Thursday',
            friday: 'Friday'
        };

        return days[day] ?? day;
    }

    async fetchData() {
        this.allStreams = await this.liveStreamsService.active();
        this.adhocStreams = this.allStreams.filter(x => !x.live_events || (x.live_events as any).length == 0);
        this.otherStreams = this.allStreams.slice(1);
        this.ready = true;
        this.updateActiveLiveEvents();

        if (isClientSide()) {
            setTimeout(() => {
                let days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
                let today = days[new Date().getDay()];
                let feedTag = document.querySelector(`[data-day-anchor="${today}"]`);

                if (!feedTag)
                    return;

                if (document.body.scrollTop < 5) {

                    if (feedTag.nextElementSibling) {
                        feedTag.nextElementSibling.scrollIntoView({
                            block: "center"
                        });
                    } else {
                        feedTag.scrollIntoView({
                            block: "start"
                        });
                    }
                }
            }, 50);
        }
    }

    get timeZone() {
        return this.useTimezone;
    }

    set timeZone(value: string) {
        this.switchTimezone(value);
    }

    getStreamLink(event: ApiLiveSchedule) {
        const eventStream = this.getEventStream(event);
        return eventStream && `/live/streams/${ eventStream.id }`;
    }

    airTimeForItem(item: ApiLiveSchedule) {
        return item.air_time_unix ? Number(item.air_time_unix) * 1000 : undefined;
    }
}
